import { GetFeedbackPageData } from "__generated__/types";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalContentLayout } from "common/components/layouts/NormalContentLayout";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { NoDataError } from "common/components/NoDataError";
import { SimpleTabs } from "common/components/tabs/SimpleTabs";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { questionsBreadcrumb } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import * as React from "react";
import { FeedbackListItem } from "../feedback/FeedbackListItem";
import {
  getFeedbackTitle,
  isAnswered,
  sortFeedbackList,
} from "../feedback/util";

const DATA_QUERY = gql`
  query GetFeedbackPageData {
    feedbackList(filter: { onlySubmittedByMe: true }) {
      id
      givenOn {
        ... on FeedbackGivenOnFolder {
          elementId
          folder {
            id
            title
          }
        }
        ... on FeedbackGivenOnKurssloeyfe {
          elementId
          kurssloeyfe {
            id
            title
          }
        }
        ... on FeedbackGivenOnSimulationCase {
          elementId
          simulationCase {
            id
            title
          }
        }
      }
      messages {
        submittedAt
        submittedBy {
          id
        }
        body {
          isMarkdown
          content
        }
      }
    }
  }
`;

export const FeedbackPage: React.FC = () => {
  const { loadingOrError, data } =
    useSimpleQuery<GetFeedbackPageData>(DATA_QUERY);

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.feedbackList) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const sortedFeedbackList = [...data.feedbackList];
  sortedFeedbackList.sort(sortFeedbackList);

  const answeredFeedbacks = sortedFeedbackList.filter(
    (e) => isAnswered(e) && e.givenOn,
  );
  const unAnsweredFeedbacks = sortedFeedbackList.filter(
    (e) => !isAnswered(e) && e.givenOn,
  );

  return (
    <NormalContentLayout
      title="Mine tilbakemeldinger"
      breadcrumbItems={questionsBreadcrumb}
    >
      <SimpleTabs
        content={[
          {
            title: "Besvarte",
            content: (
              <>
                {answeredFeedbacks.length === 0 ? (
                  <p>Ingen besvarte tilbakemeldinger</p>
                ) : (
                  <ListContainer>
                    {answeredFeedbacks.map((feedback) => (
                      <ListItem key={feedback.id}>
                        <FeedbackListItem
                          id={feedback.id}
                          title={getFeedbackTitle(feedback)}
                          canAnswer={true}
                          isQuestion={!feedback.givenOn}
                          answerButtonTitle={"Ny melding"}
                        />
                      </ListItem>
                    ))}
                  </ListContainer>
                )}
              </>
            ),
          },
          {
            title: "Ubesvarte",
            content: (
              <>
                {unAnsweredFeedbacks.length === 0 ? (
                  <p>Ingen ubesvarte tilbakemeldinger</p>
                ) : (
                  <ListContainer>
                    {unAnsweredFeedbacks.map((feedback) => (
                      <ListItem key={feedback.id}>
                        <FeedbackListItem
                          id={feedback.id}
                          title={getFeedbackTitle(feedback)}
                          canAnswer={true}
                          isQuestion={!feedback.givenOn}
                          answerButtonTitle={"Ny melding"}
                        />
                      </ListItem>
                    ))}
                  </ListContainer>
                )}
              </>
            ),
          },
        ]}
      />
    </NormalContentLayout>
  );
};
